import { AloftPrice, SUBJECT_PRICE_PER_SF_ALERT_TYPES, isDefined } from '@aloft/toolkit-shared';
import { Block, Flex, Icon, SimpleInput, Tooltip, Typography, theme } from '@aloft/toolkit-sui';

import { useMemo } from 'react';

import { ContentBlock } from '../../../components/Analysis/ColumnComponents/ContentBlock.tsx';
import {
  getVacantLandChartData,
  getVacantLandSalesExponentialFit,
} from '../../../components/graphs/costApproach/utils.ts';
import { useVacantLandSales } from '../../../hooks/useVacantLandSales.tsx';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import {
  selectSummaryDataDisplayById,
} from '../../../redux/features/selectors/costApproachSelectors/selectSubjectSummaryData.ts';
import { selectNumericSiteArea } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { setPerSf } from '../../../redux/features/thunks/setPerSf.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { useAlerts } from '../../referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { schPrice } from '../types.ts';

type SummarySectionProps = {
  id: string;
};

export const SummarySection = ({ id }: SummarySectionProps) => {
  const isSubject = id === 'subject' || id === 'subjectArv';
  const dispatch = useAppDispatch();
  const { actualAgeDisplay, yearBuiltDisplay, siteSizeDisplay, pricePerSfDisplay } = useAppSelector(
    (state: RootState) => selectSummaryDataDisplayById(state, id),
  );
  const alerts = useAlerts();
  const { errors } = useAppraisifyValidation();
  const pricePerSfError = errors?.Analysis?.opinionOfSiteValue;
  const subjectSiteArea = useAppSelector((state: RootState) =>
    selectNumericSiteArea(state, 'subject'),
  );
  const vacantLandSales = useVacantLandSales();

  const hasSiteValueAlert = useMemo(() => {
    const allAlerts = [...alerts.depreciatedCost, ...alerts.adjustments];
    const alertPropertyID = id === 'subjectArv' ? 'subject' : id;

    return allAlerts.some(
      (alert) =>
        SUBJECT_PRICE_PER_SF_ALERT_TYPES.includes(alert.type) &&
        alert.status === 'active' &&
        alert.propertyID === alertPropertyID,
    );
  }, [alerts, id]);
  const hasSubjectPricePerSfValue = useMemo(() => {
    const parsedValue = schPrice.safeParse(pricePerSfDisplay);
    return parsedValue.success && parsedValue.data?.unitAmount && parsedValue.data.unitAmount > 0;
  }, [pricePerSfDisplay]);
  const isPricePerSfEmpty = isSubject && !hasSubjectPricePerSfValue;
  const hasAlertWarning = hasSiteValueAlert && !isPricePerSfEmpty;
  const projectedVacantLandCostPerSf = useMemo(() => {
    if (!isSubject || !subjectSiteArea) return undefined;

    const vacantLandSalesChartData = getVacantLandChartData(vacantLandSales);
    if (!vacantLandSalesChartData || vacantLandSalesChartData.length < 2) return undefined;

    const { getCostPerSFFromSiteSize } = getVacantLandSalesExponentialFit(vacantLandSalesChartData);
    return getCostPerSFFromSiteSize(subjectSiteArea);
  }, [isSubject, subjectSiteArea, vacantLandSales]);
  const projectedVacantLandCostPerSfDisplay = isDefined(projectedVacantLandCostPerSf)
    ? Math.round(projectedVacantLandCostPerSf).toLocaleString('en-US')
    : undefined;
  const warningTooltipLabel = (
    <Typography variant="body1" color="textOffset">
      {isPricePerSfEmpty && isDefined(projectedVacantLandCostPerSfDisplay)
        ? `Vacant land value is projected at $${projectedVacantLandCostPerSfDisplay}/sf. The site value price per square foot must be greater than the estimated vacant land value.`
        : 'There is an active alert related to site value or price per square foot.'}
    </Typography>
  );

  const handlePerSfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = schPrice.safeParse(event.target.value);
    const parsedValue = value.success
      ? (value.data ?? AloftPrice.zero.toJSON())
      : AloftPrice.zero.toJSON();
    dispatch(setPerSf(parsedValue))
      .unwrap()
      .catch(() => undefined);
  };

  return (
    <ContentBlock>
      <Flex flexDirection="row" width="100%">
        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingRight="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Actual Age
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold' }}
          >
            {actualAgeDisplay}
          </Typography>
        </Flex>

        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingX="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Year Built
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold' }}
          >
            {yearBuiltDisplay}
          </Typography>
        </Flex>

        <Flex
          flexDirection="column"
          gap="0.25rem"
          flex={1}
          paddingX="1rem"
          borderRightWidth="1px"
          borderColor="strokeDefault"
          borderStyle="solid"
          alignItems="center"
        >
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Site Size
          </Typography>
          <Typography
            variant="body1"
            color="textTertiary"
            __dangerDoNotUseSx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
          >
            {siteSizeDisplay}
          </Typography>
        </Flex>

        <Flex flexDirection="column" gap="0.25rem" flex={1} paddingLeft="1rem" alignItems="center">
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
          >
            Price per SF
          </Typography>
          {isSubject ? (
            <Block
              className="price-per-sf-input"
              paddingX="0.5rem"
              paddingY="0.25rem"
              borderWidth="1px"
              borderColor="strokeLight"
              borderStyle="solid"
              borderRadius="4px"
              backgroundColor="surface1"
              width="fit-content"
            >
              <Flex flexDirection="row" alignItems="center" gap="0.5rem" flexWrap="nowrap">
                <SimpleInput
                  id={`price-per-sf-${id}`}
                  type="number"
                  value={pricePerSfDisplay}
                  onChange={handlePerSfChange}
                  precision={2}
                  required
                  size="xs"
                  variant="grid"
                  useSeparator
                  width="5rem"
                  disabled={!isDefined(subjectSiteArea)}
                  invalid={!!pricePerSfError}
                  errorMessage={pricePerSfError?.message}
                  leftAddon={
                    <Typography variant="body1" color="textDisabled">
                      $
                    </Typography>
                  }
                  rightAddon={
                    <Typography variant="body1" color="textDisabled">
                      /sf
                    </Typography>
                  }
                />
                {hasAlertWarning || isPricePerSfEmpty ? (
                  <Tooltip label={warningTooltipLabel} placement="right" maxWidth="26rem">
                    <Block
                      width="20px"
                      height="20px"
                      __dangerDoNotUseSx={{
                        backgroundColor: theme.colors.alertWarning,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="warning" color="textOffset" size="md" />
                    </Block>
                  </Tooltip>
                ) : (
                  <Block
                    title="Price per square foot is valid."
                    width="20px"
                    height="20px"
                    __dangerDoNotUseSx={{
                      backgroundColor: theme.colors.alertSuccess,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon icon="check" color="textOffset" size="sm" />
                  </Block>
                )}
              </Flex>
            </Block>
          ) : (
            <Flex flexDirection="row" alignItems="center" gap="0.5rem" flexWrap="nowrap">
              <Typography
                variant="body1"
                color="textTertiary"
                __dangerDoNotUseSx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
              >
                {pricePerSfDisplay}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ContentBlock>
  );
};
